import { type FunctionReference, anyApi } from "convex/server";
import { type GenericId as Id } from "convex/values";

export const api: PublicApiType = anyApi as unknown as PublicApiType;
export const internal: InternalApiType = anyApi as unknown as InternalApiType;

export type PublicApiType = {
  aiUsageLogs: {
    getCostByContext: FunctionReference<
      "query",
      "public",
      { sinceDays?: number },
      any
    >;
    logAiUsage: FunctionReference<
      "mutation",
      "public",
      {
        clientId?: Id<"clients">;
        completionTokens: number;
        context?: string;
        firmId?: Id<"firms">;
        formType?: string;
        modelName: string;
        promptTokens: number;
        submissionId?: Id<"submissions">;
        totalTokens: number;
      },
      any
    >;
    logOcrTransaction: FunctionReference<
      "mutation",
      "public",
      {
        completionTokens: number;
        firmId: Id<"firms">;
        formType?: string;
        modelName: string;
        promptTokens: number;
        submissionId?: Id<"submissions">;
        totalTokens: number;
      },
      any
    >;
    listForFirm: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    getFormStats: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    listAll: FunctionReference<"query", "public", Record<string, never>, any>;
    getCostByClient: FunctionReference<
      "query",
      "public",
      { clientIds: Array<Id<"clients">>; firmId: Id<"firms"> },
      any
    >;
  };
  clients: {
    getClient: FunctionReference<
      "query",
      "public",
      { clientId: Id<"clients"> },
      any
    >;
    setIntakeFieldOverride: FunctionReference<
      "mutation",
      "public",
      {
        clientId: Id<"clients">;
        externalId: string;
        state: "ask" | "skip" | "default";
      },
      any
    >;
    setClientRequiredDocOverride: FunctionReference<
      "mutation",
      "public",
      {
        added: Array<{
          custom?: boolean;
          key: string;
          label?: string;
          required?: boolean;
        }>;
        clientId: Id<"clients">;
        removed: Array<string>;
      },
      any
    >;
    getClientByLegacyId: FunctionReference<
      "query",
      "public",
      { legacyId: string },
      any
    >;
    listClients: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    getClientForFirm: FunctionReference<
      "query",
      "public",
      { clientId: Id<"clients">; firmId: Id<"firms"> },
      any
    >;
    listClientsWithActiveRevisions: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    insertClient: FunctionReference<
      "mutation",
      "public",
      {
        firmId: Id<"firms">;
        firstName: string;
        lastName: string;
        notificationProfileId?: Id<"notificationProfiles">;
        preferredLanguage?: string;
      },
      any
    >;
    updateClient: FunctionReference<
      "mutation",
      "public",
      {
        clientId: Id<"clients">;
        firmId: Id<"firms">;
        updates: {
          email?: string;
          fillOnlyLegalDocuments?: Array<Id<"legalDocuments">>;
          firstName?: string;
          lastName?: string;
          legalDocuments?: Array<Id<"legalDocuments">>;
          notes?: any;
          notificationProfileId?: Id<"notificationProfiles"> | null;
          phoneNumber?: string;
          preferredLanguage?: string;
          primaryFormDefinitionId?: Id<"formDefinitions">;
          status?: string;
        };
      },
      any
    >;
    deleteClient: FunctionReference<
      "mutation",
      "public",
      { clientId: Id<"clients">; firmId: Id<"firms"> },
      any
    >;
    recordEmailConsent: FunctionReference<
      "mutation",
      "public",
      { clientId: Id<"clients"> },
      any
    >;
    searchClients: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms">; searchName: string },
      any
    >;
  };
  errorLogs: {
    logError: FunctionReference<
      "mutation",
      "public",
      {
        clientId?: Id<"clients">;
        context: string;
        details?: any;
        firmId?: Id<"firms">;
        message?: string;
        pageUrl?: string;
        replayUrl?: string;
        source: string;
        submissionId?: Id<"submissions">;
      },
      any
    >;
    logServerError: FunctionReference<
      "action",
      "public",
      { context: string; details?: any; firmId?: Id<"firms">; message: string },
      any
    >;
  };
  feedback: {
    saveFeedback: FunctionReference<
      "mutation",
      "public",
      { comment?: string; rating?: number; submissionId: Id<"submissions"> },
      any
    >;
    hasFeedback: FunctionReference<
      "query",
      "public",
      { submissionId: Id<"submissions"> },
      any
    >;
    insertDashboardFeedback: FunctionReference<
      "mutation",
      "public",
      {
        email?: string;
        firmId: Id<"firms">;
        message?: string;
        title: string;
        type: string;
      },
      any
    >;
    insertMonthlyFeedback: FunctionReference<
      "mutation",
      "public",
      {
        firmId: Id<"firms">;
        formCount: number;
        otherFeedback?: string;
        rating: number;
        selectedOptions?: Array<string>;
      },
      any
    >;
    listAllFeedback: FunctionReference<
      "query",
      "public",
      Record<string, never>,
      any
    >;
    getFeedbackStats: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
  };
  firms: {
    attachImportedForm: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        firmId: Id<"firms">;
        formId: Id<"importedForms">;
      },
      any
    >;
    clearBranding: FunctionReference<
      "mutation",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    decrementMonthlyClients: FunctionReference<
      "mutation",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    deleteEmailOverride: FunctionReference<
      "mutation",
      "public",
      { firmId: Id<"firms">; formDefinitionId: Id<"formDefinitions"> },
      any
    >;
    deleteImportedForm: FunctionReference<
      "mutation",
      "public",
      { firmId: Id<"firms">; formId: Id<"importedForms"> },
      any
    >;
    detachImportedForm: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        firmId: Id<"firms">;
        formId: Id<"importedForms">;
      },
      any
    >;
    etatDeLaCleApi: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    generateApiKey: FunctionReference<
      "mutation",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    generateBrandingLogoUploadUrl: FunctionReference<
      "mutation",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    getBranding: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    getCoordonnees: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    getEmailOverrides: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    getEmailSettings: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    getFirmByApiKey: FunctionReference<
      "query",
      "public",
      { apiKey: string },
      any
    >;
    getFirmBySubdomain: FunctionReference<
      "query",
      "public",
      { subdomain: string },
      any
    >;
    getFirmByWorkosUserId: FunctionReference<
      "query",
      "public",
      { workosUserId: string },
      any
    >;
    getFirmDisplayName: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    getFormBranding: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    getFormSubdomain: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    getImmOverride: FunctionReference<
      "query",
      "public",
      { demandeTypeId: Id<"demandeTypes">; firmId: Id<"firms">; role?: string },
      any
    >;
    getMonthlyClientQuota: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    getSubscriptionInfo: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    listImportedForms: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    markRecentlyUsed: FunctionReference<
      "mutation",
      "public",
      {
        documents?: Array<string>;
        firmId: Id<"firms">;
        questions?: Array<string>;
      },
      any
    >;
    recordAccountEmail: FunctionReference<
      "mutation",
      "public",
      { email: string },
      any
    >;
    requestBrandingInfo: FunctionReference<
      "mutation",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    saveImportedForm: FunctionReference<
      "mutation",
      "public",
      {
        documents: Array<{ docKey: string | null; label: string }>;
        firmId: Id<"firms">;
        formiofied?: boolean;
        name: string;
        questions: Array<{
          dependsOn?: any;
          externalId: string | null;
          guidance?: {
            example?: string;
            help?: string;
            indication?: string;
            placeholder?: string;
            successMessage?: string;
            whyImportantConsequence?: string;
            whyImportantReason?: string;
          };
          key?: string;
          label: string;
          multiEntryFields?: any;
          options?: any;
          section?: string;
          type: string;
        }>;
      },
      any
    >;
    setAutoFormiofy: FunctionReference<
      "mutation",
      "public",
      { enabled: boolean; firmId: Id<"firms"> },
      any
    >;
    setBranding: FunctionReference<
      "mutation",
      "public",
      {
        branding: {
          accentColor: string;
          backgroundColor: string;
          cardColor: string;
          logoFileName?: string;
          logoStorageId?: Id<"_storage">;
          presentation: {
            greeting: string;
            message: string;
            showChecklist: boolean;
          };
          primaryColor: string;
        };
        firmId: Id<"firms">;
        subdomain?: string;
      },
      any
    >;
    setCoordonnees: FunctionReference<
      "mutation",
      "public",
      {
        coordonnees: {
          adresse?: {
            appartement?: string;
            codePostal?: string;
            numeroCivique?: string;
            pays?: string;
            province?: string;
            rue?: string;
            ville?: string;
          };
          courriel?: string;
          nom?: string;
          telecopieur?: string;
          telecopieurIndicatifPays?: string;
          telephone?: string;
          telephoneIndicatifPays?: string;
        };
        firmId: Id<"firms">;
      },
      any
    >;
    setDocumentDependsOn: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        dependsOn?: any;
        docKey: string;
        firmId: Id<"firms">;
        role?: string;
      },
      any
    >;
    setDocumentNamingTemplate: FunctionReference<
      "mutation",
      "public",
      { firmId: Id<"firms">; template: string },
      any
    >;
    setFirmDocumentLibrary: FunctionReference<
      "mutation",
      "public",
      {
        docKey: string;
        extraInstructions?: string | null;
        firmId: Id<"firms">;
        maxCount?: number | null;
      },
      any
    >;
    setImmOverride: FunctionReference<
      "mutation",
      "public",
      {
        added: Array<Id<"legalDocuments">>;
        demandeTypeId: Id<"demandeTypes">;
        firmId: Id<"firms">;
        removed: Array<Id<"legalDocuments">>;
        role?: string;
      },
      any
    >;
    setIntakeDisabledFields: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        externalIds: Array<string>;
        firmId: Id<"firms">;
        role?: string;
      },
      any
    >;
    setIntakeQuestionDependsOn: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        dependsOn?: any;
        externalId: string;
        firmId: Id<"firms">;
        role?: string;
      },
      any
    >;
    setIntakeQuestionGuidance: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        externalId: string;
        firmId: Id<"firms">;
        patch: {
          example?: string;
          help?: string;
          indication?: string;
          label?: string;
          multiEntryFields?: any;
          options?: any;
          placeholder?: string;
          shortLabel?: string;
          successMessage?: string;
          whyImportantConsequence?: string;
          whyImportantReason?: string;
        };
        role?: string;
      },
      any
    >;
    setIntakeQuestionOcrFill: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        externalId: string;
        fill: { docKey: string } | null;
        firmId: Id<"firms">;
        role?: string;
      },
      any
    >;
    setIntakeQuestionOrder: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        externalIds: Array<string>;
        firmId: Id<"firms">;
        role?: string;
      },
      any
    >;
    setIntakeQuestionOverrides: FunctionReference<
      "mutation",
      "public",
      {
        added: Array<{
          custom?: boolean;
          externalId: string;
          label?: string;
          multiEntryFields?: any;
          options?: any;
          required?: boolean;
          section?: string;
          type?: string;
        }>;
        demandeTypeId: Id<"demandeTypes">;
        firmId: Id<"firms">;
        labels: Record<string, string>;
        required: Record<string, boolean>;
        role?: string;
      },
      any
    >;
    setIntakeQuestionTranslations: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        entries: Array<{ externalId: string; fields: any; language: string }>;
        firmId: Id<"firms">;
        role?: string;
      },
      any
    >;
    setIntakeQuestionType: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        externalId: string;
        firmId: Id<"firms">;
        role?: string;
        type: string | null;
      },
      any
    >;
    setRequiredDocDescription: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        description: string;
        docKey: string;
        firmId: Id<"firms">;
        role?: string;
      },
      any
    >;
    setRequiredDocGuidance: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        docKey: string;
        firmId: Id<"firms">;
        patch: {
          example?: string;
          successMessage?: string;
          whyImportantConsequence?: string;
          whyImportantReason?: string;
        };
        role?: string;
      },
      any
    >;
    setRequiredDocInstructions: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        docKey: string;
        firmId: Id<"firms">;
        instructions: string;
        role?: string;
      },
      any
    >;
    setRequiredDocLabel: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        docKey: string;
        firmId: Id<"firms">;
        label: string;
        role?: string;
      },
      any
    >;
    setRequiredDocMaxCount: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        docKey: string;
        firmId: Id<"firms">;
        maxCount: number | null;
        role?: string;
      },
      any
    >;
    setRequiredDocOverrides: FunctionReference<
      "mutation",
      "public",
      {
        added: Array<{
          custom?: boolean;
          key: string;
          label?: string;
          required?: boolean;
        }>;
        demandeTypeId: Id<"demandeTypes">;
        firmId: Id<"firms">;
        removed: Array<string>;
        role?: string;
      },
      any
    >;
    setRequiredDocTranslations: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        entries: Array<{ docKey: string; fields: any; language: string }>;
        firmId: Id<"firms">;
        role?: string;
      },
      any
    >;
    setSectionRename: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        firmId: Id<"firms">;
        newTitle: string | null;
        originalTitle: string;
        role?: string;
      },
      any
    >;
    updateEmailSettings: FunctionReference<
      "mutation",
      "public",
      {
        firmId: Id<"firms">;
        settings: {
          generalNotificationEmail?: string;
          physicalMailingAddress?: string;
          reminderCadence: {
            firstAfterDays: number;
            maxReminders: number;
            repeatEveryDays: number;
          };
          remindersEnabled: boolean;
        };
      },
      any
    >;
    upsertEmailOverride: FunctionReference<
      "mutation",
      "public",
      {
        email: string;
        firmId: Id<"firms">;
        formDefinitionId: Id<"formDefinitions">;
      },
      any
    >;
  };
  legalDocuments: {
    buildFillJobPayload: FunctionReference<
      "query",
      "public",
      { clientId: Id<"clients">; legalDocumentId: Id<"legalDocuments"> },
      any
    >;
    casesDesFormulaires: FunctionReference<
      "query",
      "public",
      { clientId: Id<"clients"> },
      any
    >;
    deleteGeneratedDoc: FunctionReference<
      "mutation",
      "public",
      { clientId: Id<"clients">; legalDocumentId: Id<"legalDocuments"> },
      any
    >;
    getCanonicalQuestionUniverse: FunctionReference<
      "query",
      "public",
      Record<string, never>,
      any
    >;
    getGeneratedDocUrl: FunctionReference<
      "query",
      "public",
      { storageId: Id<"_storage"> },
      any
    >;
    getIntakeCatalogForDemandeType: FunctionReference<
      "query",
      "public",
      { demandeTypeId: Id<"demandeTypes">; firmId: Id<"firms">; role?: string },
      any
    >;
    getIntakeForClient: FunctionReference<
      "query",
      "public",
      { clientId: Id<"clients"> },
      any
    >;
    getIntakeForLegalDocs: FunctionReference<
      "query",
      "public",
      { firmId?: Id<"firms">; legalDocumentIds: Array<Id<"legalDocuments">> },
      any
    >;
    getLegalDocumentsByIds: FunctionReference<
      "query",
      "public",
      { ids: Array<Id<"legalDocuments">> },
      any
    >;
    getPooledAnswers: FunctionReference<
      "query",
      "public",
      { clientId: Id<"clients">; submissionId?: Id<"submissions"> },
      any
    >;
    listGeneratedDocs: FunctionReference<
      "query",
      "public",
      { clientId: Id<"clients"> },
      any
    >;
    listGeneratedDocsForClients: FunctionReference<
      "query",
      "public",
      { clientIds: Array<Id<"clients">> },
      any
    >;
    listLegalDocuments: FunctionReference<
      "query",
      "public",
      { language?: string },
      any
    >;
    poserDocumentGenere: FunctionReference<
      "mutation",
      "public",
      {
        clientId: Id<"clients">;
        edition?: string;
        error?: string;
        errorCode?: string;
        legalDocumentId: Id<"legalDocuments">;
        status: string;
        storageId?: Id<"_storage">;
      },
      any
    >;
    setFieldMappings: FunctionReference<
      "mutation",
      "public",
      { fieldMappings: any; legalDocumentId: Id<"legalDocuments"> },
      any
    >;
  };
  questions: {
    addFirmQuestion: FunctionReference<
      "mutation",
      "public",
      {
        example?: string;
        firmId: Id<"firms">;
        help?: string;
        indication?: string;
        label: string;
        multiEntryFields?: any;
        options?: any;
        placeholder?: string;
        preferredKey?: string;
        successMessage?: string;
        type: string;
        whyImportantConsequence?: string;
        whyImportantReason?: string;
      },
      any
    >;
    deleteFirmQuestion: FunctionReference<
      "mutation",
      "public",
      { externalId: string; firmId: Id<"firms"> },
      any
    >;
    describeAnswerKeys: FunctionReference<
      "query",
      "public",
      { externalIds: Array<string> },
      any
    >;
    getDistinctSections: FunctionReference<
      "query",
      "public",
      { formDefinitionId: Id<"formDefinitions"> },
      any
    >;
    getFormQuestions: FunctionReference<
      "query",
      "public",
      { formDefinitionId: Id<"formDefinitions"> },
      any
    >;
    getGuidancePresence: FunctionReference<
      "query",
      "public",
      { externalIds: Array<string> },
      any
    >;
    getOptionPresets: FunctionReference<
      "query",
      "public",
      Record<string, never>,
      any
    >;
    getOwnFormQuestions: FunctionReference<
      "query",
      "public",
      { formDefinitionId: Id<"formDefinitions"> },
      any
    >;
    getQuestionsByExternalIds: FunctionReference<
      "query",
      "public",
      { externalIds: Array<string> },
      any
    >;
    listCatalogQuestions: FunctionReference<
      "query",
      "public",
      Record<string, never>,
      any
    >;
    replaceFormQuestions: FunctionReference<
      "mutation",
      "public",
      {
        formDefinitionId: Id<"formDefinitions">;
        rows: Array<{
          dependsOn?: any;
          exampleOverride?: string;
          indicationOverride?: string;
          labelOverride?: string;
          orderIndex: number;
          questionKey: string;
          requiredOverride?: boolean;
          section?: string;
          sectionTranslations?: any;
        }>;
      },
      any
    >;
    updateFirmQuestion: FunctionReference<
      "mutation",
      "public",
      {
        externalId: string;
        firmId: Id<"firms">;
        label?: string;
        multiEntryFields?: any;
        options?: any;
        type?: string;
      },
      any
    >;
    upsertQuestionsBatch: FunctionReference<
      "mutation",
      "public",
      {
        questions: Array<{
          example?: string;
          externalId: string;
          firmId?: Id<"firms">;
          help?: string;
          indication?: string;
          isRequired?: boolean;
          label: string;
          multiEntryFields?: any;
          options?: any;
          placeholder?: string;
          shortLabel?: string;
          successMessage?: string;
          type: string;
          whyImportantConsequence?: string;
          whyImportantReason?: string;
        }>;
      },
      any
    >;
  };
  submissionDocuments: {
    deleteDocument: FunctionReference<
      "mutation",
      "public",
      { documentId: Id<"submissionDocuments"> },
      any
    >;
    generateUploadUrl: FunctionReference<"mutation", "public", any, any>;
    getDocumentUrl: FunctionReference<
      "query",
      "public",
      { storageId: Id<"_storage"> },
      any
    >;
    listDocuments: FunctionReference<
      "query",
      "public",
      { submissionId: Id<"submissions"> },
      any
    >;
    marquerLecture: FunctionReference<
      "mutation",
      "public",
      {
        contentHash?: string;
        documentId: Id<"submissionDocuments">;
        lecture?: {
          copieDe?: Id<"submissionDocuments">;
          etat: string;
          faiteLe: number;
          modele?: string;
          pourquoi?: string;
          questionsVues: Array<string>;
          resume?: Array<string>;
          titulaire?: string;
          trouve: Array<{ citation?: string; externalId: string; valeur: any }>;
        };
      },
      any
    >;
    registerDocument: FunctionReference<
      "mutation",
      "public",
      {
        fileType?: string;
        name: string;
        storageId: Id<"_storage">;
        submissionId: Id<"submissions">;
        verificationFeedback?: string;
        verificationStatus?: string;
      },
      any
    >;
    valeursEnAttente: FunctionReference<
      "query",
      "public",
      { clientId: Id<"clients"> },
      any
    >;
  };
  submissions: {
    accepterValeursLues: FunctionReference<
      "mutation",
      "public",
      {
        acceptees: Array<{
          citation?: string;
          documentId: Id<"submissionDocuments">;
          externalId: string;
          remplacer?: boolean;
          valeur: any;
        }>;
        refusees: Array<{
          clefValeur?: string;
          documentId: Id<"submissionDocuments">;
          externalId: string;
        }>;
        reprises?: Array<{ depuis: string; externalId: string; valeur: any }>;
        submissionId: Id<"submissions">;
      },
      any
    >;
    checkGroupCompletion: FunctionReference<
      "query",
      "public",
      { submissionId: Id<"submissions"> },
      any
    >;
    completeSubmission: FunctionReference<
      "mutation",
      "public",
      { startedAt?: string; submissionId: Id<"submissions"> },
      any
    >;
    deleteSubmission: FunctionReference<
      "mutation",
      "public",
      { submissionId: Id<"submissions"> },
      any
    >;
    getSubmission: FunctionReference<
      "query",
      "public",
      { submissionId: Id<"submissions"> },
      any
    >;
    initGroupedSubmissions: FunctionReference<
      "mutation",
      "public",
      {
        clientId: Id<"clients">;
        firmId: Id<"firms">;
        formGroup: string;
        forms: Array<{
          formDefinitionId: Id<"formDefinitions">;
          formType?: string;
        }>;
        preferredLanguage?: string;
        title: string;
      },
      any
    >;
    initSubmission: FunctionReference<
      "mutation",
      "public",
      {
        clientId: Id<"clients">;
        firmId: Id<"firms">;
        formType?: string;
        preferredLanguage?: string;
        title: string;
      },
      any
    >;
    listClientSubmissions: FunctionReference<
      "query",
      "public",
      { clientId: Id<"clients">; firmId: Id<"firms"> },
      any
    >;
    markStarted: FunctionReference<
      "mutation",
      "public",
      { device?: string; submissionId: Id<"submissions"> },
      any
    >;
    retirerReponse: FunctionReference<
      "mutation",
      "public",
      { externalId: string; submissionId: Id<"submissions"> },
      any
    >;
    saveAnswer: FunctionReference<
      "mutation",
      "public",
      {
        questionId: string;
        sansTraduction?: boolean;
        submissionId: Id<"submissions">;
        translatedValue?: any;
        value: any;
      },
      any
    >;
    saveInitialAnswers: FunctionReference<
      "mutation",
      "public",
      { answers: any; submissionId: Id<"submissions">; translatedValues?: any },
      any
    >;
    setCurrentQuestion: FunctionReference<
      "mutation",
      "public",
      { questionId: string; submissionId: Id<"submissions"> },
      any
    >;
    updateSubmissionFromDashboard: FunctionReference<
      "mutation",
      "public",
      {
        submissionId: Id<"submissions">;
        updates: {
          answers?: any;
          metadata?: any;
          skippedSections?: any;
          status?: string;
          title?: string;
        };
      },
      any
    >;
  };
  supplementRequests: {
    getSupplementRequest: FunctionReference<
      "query",
      "public",
      { id: Id<"supplementRequests"> },
      any
    >;
    saveSupplementAnswer: FunctionReference<
      "mutation",
      "public",
      {
        questionId: string;
        supplementId: Id<"supplementRequests">;
        value: any;
      },
      any
    >;
    createSupplementRequest: FunctionReference<
      "mutation",
      "public",
      {
        clientId: Id<"clients">;
        firmId: Id<"firms">;
        requestedQuestions: Array<string>;
        requestedSections: Array<string>;
        submissionId: Id<"submissions">;
      },
      any
    >;
    listForSubmission: FunctionReference<
      "query",
      "public",
      { submissionId: Id<"submissions"> },
      any
    >;
    deleteSupplementRequest: FunctionReference<
      "mutation",
      "public",
      { removeAnswers?: boolean; supplementId: Id<"supplementRequests"> },
      any
    >;
    completeSupplementSubmission: FunctionReference<
      "mutation",
      "public",
      { supplementId: Id<"supplementRequests">; translatedValues?: any },
      any
    >;
  };
  formDefinitions: {
    listGlobalForms: FunctionReference<
      "query",
      "public",
      Record<string, never>,
      any
    >;
    listCustomForms: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    getFirmBaseForm: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    listFormsForSendFlow: FunctionReference<
      "query",
      "public",
      { firmId?: Id<"firms"> },
      any
    >;
    listFormsForFirm: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    createBlankForm: FunctionReference<
      "mutation",
      "public",
      {
        category: string;
        firmId: Id<"firms">;
        isBaseForm?: boolean;
        language?: string;
        name: string;
      },
      any
    >;
    renameForm: FunctionReference<
      "mutation",
      "public",
      { formId: Id<"formDefinitions">; name: string },
      any
    >;
    deleteForm: FunctionReference<
      "mutation",
      "public",
      { formId: Id<"formDefinitions"> },
      any
    >;
    getGlobalBaseForm: FunctionReference<
      "query",
      "public",
      Record<string, never>,
      any
    >;
    updateFormDefinition: FunctionReference<
      "mutation",
      "public",
      {
        formId: Id<"formDefinitions">;
        updates: {
          baseFormId?: Id<"formDefinitions">;
          category?: string;
          description?: string;
          excludedBaseSections?: Array<string>;
          isBaseForm?: boolean;
          isSelfContained?: boolean;
          language?: string;
          name?: string;
        };
      },
      any
    >;
    linkBaseForm: FunctionReference<
      "mutation",
      "public",
      {
        baseFormId?: Id<"formDefinitions">;
        customFormId: Id<"formDefinitions">;
      },
      any
    >;
    setBaseSectionToggles: FunctionReference<
      "mutation",
      "public",
      {
        customFormId: Id<"formDefinitions">;
        excludedBaseSections: Array<string>;
      },
      any
    >;
    forkForm: FunctionReference<
      "mutation",
      "public",
      {
        firmId: Id<"firms">;
        isBaseForm?: boolean;
        name?: string;
        sourceFormId: Id<"formDefinitions">;
      },
      any
    >;
    deleteBaseForm: FunctionReference<
      "mutation",
      "public",
      { formId: Id<"formDefinitions"> },
      any
    >;
  };
  questionTemplates: {
    listTemplates: FunctionReference<
      "query",
      "public",
      Record<string, never>,
      any
    >;
  };
  uploadedForms: {
    insertUploadedForm: FunctionReference<
      "mutation",
      "public",
      {
        batchId?: string;
        firmId: Id<"firms">;
        formType?: string;
        legalDocumentName?: string;
        name?: string;
        status: string;
      },
      any
    >;
    listUploadedForms: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    listActiveUploads: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    updateUploadStatus: FunctionReference<
      "mutation",
      "public",
      {
        id: Id<"uploadedForms">;
        updates: {
          error?: string;
          status?: string;
          storageId?: Id<"_storage">;
        };
      },
      any
    >;
    deleteBatch: FunctionReference<
      "mutation",
      "public",
      { batchId: string; firmId: Id<"firms"> },
      any
    >;
  };
  admin: {
    listAllFirms: FunctionReference<
      "query",
      "public",
      Record<string, never>,
      any
    >;
    getFirm: FunctionReference<"query", "public", { firmId: Id<"firms"> }, any>;
    updateFirm: FunctionReference<
      "mutation",
      "public",
      {
        firmId: Id<"firms">;
        updates: {
          apiKey?: string;
          brandingEnabled?: boolean | null;
          displayName?: string;
          membershipStatus?: string;
          monthlyClientLimit?: number | null;
          monthlyClientsRemaining?: number | null;
          notificationProfileLimit?: number | null;
          subscriptionEndDate?: number | null;
          subscriptionStartDate?: number | null;
        };
      },
      any
    >;
    createPendingFirm: FunctionReference<
      "mutation",
      "public",
      {
        displayName?: string;
        membershipStatus: string;
        monthlyClientLimit?: number;
        monthlyClientsRemaining?: number;
        pendingEmail: string;
        subscriptionEndDate?: number;
      },
      any
    >;
    attachInvitationToFirm: FunctionReference<
      "mutation",
      "public",
      {
        firmId: Id<"firms">;
        invitationSentAt: number;
        workosInvitationId: string;
      },
      any
    >;
    attachWorkosUserToFirm: FunctionReference<
      "mutation",
      "public",
      { pendingEmail: string; workosUserId: string },
      any
    >;
    cancelPendingFirm: FunctionReference<
      "mutation",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    deleteFirm: FunctionReference<
      "mutation",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    listAllClients: FunctionReference<
      "query",
      "public",
      Record<string, never>,
      any
    >;
    listSubmissionSummaries: FunctionReference<
      "query",
      "public",
      {
        paginationOpts: {
          cursor: string | null;
          endCursor?: string | null;
          id?: number;
          maximumBytesRead?: number;
          maximumRowsRead?: number;
          numItems: number;
        };
      },
      any
    >;
    listAllErrorLogs: FunctionReference<
      "query",
      "public",
      { limit?: number },
      any
    >;
    listFirmSubmissionSummaries: FunctionReference<
      "query",
      "public",
      {
        firmId: Id<"firms">;
        paginationOpts: {
          cursor: string | null;
          endCursor?: string | null;
          id?: number;
          maximumBytesRead?: number;
          maximumRowsRead?: number;
          numItems: number;
        };
      },
      any
    >;
    getAiUsageByFirm: FunctionReference<
      "query",
      "public",
      Record<string, never>,
      any
    >;
    getFirmClientsDetail: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    isCurrentUserAdmin: FunctionReference<
      "query",
      "public",
      Record<string, never>,
      any
    >;
    listAllFormFeedback: FunctionReference<
      "query",
      "public",
      Record<string, never>,
      any
    >;
    cleanupStagingData: FunctionReference<
      "mutation",
      "public",
      { samplesPerFirm: number },
      any
    >;
    getDeviceBreakdown: FunctionReference<
      "query",
      "public",
      Record<string, never>,
      any
    >;
  };
  demandeTypes: {
    attachDemandeToClient: FunctionReference<
      "mutation",
      "public",
      {
        clientId: Id<"clients">;
        demandeTypeId: Id<"demandeTypes">;
        role?: string;
      },
      any
    >;
    branchDemandeType: FunctionReference<
      "mutation",
      "public",
      { firmId: Id<"firms">; sourceDemandeTypeId: Id<"demandeTypes"> },
      any
    >;
    createFirmDemandeType: FunctionReference<
      "mutation",
      "public",
      {
        description?: string;
        firmId: Id<"firms">;
        legalDocumentIds: Array<Id<"legalDocuments">>;
        name: string;
      },
      any
    >;
    deleteFirmDemandeType: FunctionReference<
      "mutation",
      "public",
      { demandeTypeId: Id<"demandeTypes">; firmId: Id<"firms"> },
      any
    >;
    ensureClientFormEditor: FunctionReference<
      "mutation",
      "public",
      { clientId: Id<"clients">; firmId: Id<"firms"> },
      any
    >;
    getDemandeType: FunctionReference<
      "query",
      "public",
      { firmId?: Id<"firms">; id: Id<"demandeTypes"> },
      any
    >;
    getDemandeTypeBySlug: FunctionReference<
      "query",
      "public",
      { slug: string },
      any
    >;
    listDemandeTypes: FunctionReference<
      "query",
      "public",
      { category?: string; firmId?: Id<"firms"> },
      any
    >;
    setDemandeTypeName: FunctionReference<
      "mutation",
      "public",
      { demandeTypeId: Id<"demandeTypes">; firmId: Id<"firms">; name: string },
      any
    >;
    updateFirmDemandeType: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        firmId: Id<"firms">;
        updates: {
          description?: string;
          fillOnlyLegalDocumentIds?: Array<Id<"legalDocuments">>;
          legalDocumentIds?: Array<Id<"legalDocuments">>;
          name?: string;
          people?: Array<{
            fillOnlyLegalDocumentIds?: Array<Id<"legalDocuments">>;
            label?: string;
            legalDocumentIds: Array<Id<"legalDocuments">>;
            role: string;
          }>;
        };
      },
      any
    >;
  };
  documents: {
    listDocuments: FunctionReference<
      "query",
      "public",
      { firmId?: Id<"firms"> },
      any
    >;
    addFirmDocument: FunctionReference<
      "mutation",
      "public",
      { firmId: Id<"firms">; name: string },
      any
    >;
    deleteFirmDocument: FunctionReference<
      "mutation",
      "public",
      { docKey: string; firmId: Id<"firms"> },
      any
    >;
    getDocumentLibrary: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    getDocumentsByKeys: FunctionReference<
      "query",
      "public",
      { firmId?: Id<"firms">; keys: Array<string> },
      any
    >;
  };
  notificationProfiles: {
    listNotificationProfiles: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    getNotificationProfileLimit: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    createNotificationProfile: FunctionReference<
      "mutation",
      "public",
      { email: string; firmId: Id<"firms">; name: string },
      any
    >;
    updateNotificationProfile: FunctionReference<
      "mutation",
      "public",
      {
        firmId: Id<"firms">;
        profileId: Id<"notificationProfiles">;
        updates: { email?: string; name?: string };
      },
      any
    >;
    deleteNotificationProfile: FunctionReference<
      "mutation",
      "public",
      { firmId: Id<"firms">; profileId: Id<"notificationProfiles"> },
      any
    >;
  };
  demandes: {
    addPersonToDemande: FunctionReference<
      "mutation",
      "public",
      {
        demandeId: Id<"demandes">;
        demandeTypeId?: Id<"demandeTypes">;
        firmId: Id<"firms">;
        firstName?: string;
        lastName?: string;
        notificationProfileId?: Id<"notificationProfiles">;
        role: string;
        roleLabel?: string;
      },
      any
    >;
    createDemande: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId?: Id<"demandeTypes">;
        firmId: Id<"firms">;
        langueFormulaires?: "fr" | "en";
        name?: string;
      },
      any
    >;
    createDemandeFromType: FunctionReference<
      "mutation",
      "public",
      {
        demandeTypeId: Id<"demandeTypes">;
        firmId: Id<"firms">;
        langueFormulaires?: "fr" | "en";
        name?: string;
        notificationProfileId?: Id<"notificationProfiles">;
        preferredLanguage?: string;
      },
      any
    >;
    definirLangueFormulaires: FunctionReference<
      "mutation",
      "public",
      { demandeId: Id<"demandes">; langue: "fr" | "en" },
      any
    >;
    definirLangueQuestionnaire: FunctionReference<
      "mutation",
      "public",
      { demandeId: Id<"demandes">; langue: "fr" | "en" | "es" },
      any
    >;
    editionFormulaires: FunctionReference<
      "query",
      "public",
      { demandeId: Id<"demandes"> },
      any
    >;
    getDemande: FunctionReference<
      "query",
      "public",
      { demandeId: Id<"demandes"> },
      any
    >;
    listDemandes: FunctionReference<
      "query",
      "public",
      { firmId: Id<"firms"> },
      any
    >;
    removePersonFromDemande: FunctionReference<
      "mutation",
      "public",
      { clientId: Id<"clients">; firmId: Id<"firms"> },
      any
    >;
    renameDemande: FunctionReference<
      "mutation",
      "public",
      { demandeId: Id<"demandes">; firmId: Id<"firms">; name: string },
      any
    >;
    setPersonRole: FunctionReference<
      "mutation",
      "public",
      { clientId: Id<"clients">; firmId: Id<"firms">; role: string },
      any
    >;
    supprimerDemande: FunctionReference<
      "mutation",
      "public",
      { demandeId: Id<"demandes"> },
      any
    >;
  };
  generateFill: {
    generateFilledPdfFromAnswers: FunctionReference<
      "action",
      "public",
      { answers: any; legalDocumentId: Id<"legalDocuments"> },
      any
    >;
    generateFilledPdf: FunctionReference<
      "action",
      "public",
      { clientId: Id<"clients">; legalDocumentId: Id<"legalDocuments"> },
      any
    >;
  };
  linkTrackerStore: {
    listLinkChecks: FunctionReference<
      "query",
      "public",
      Record<string, never>,
      any
    >;
    applySuggestedUrl: FunctionReference<
      "mutation",
      "public",
      { legalDocumentId: Id<"legalDocuments">; url: string },
      any
    >;
    requestRecheck: FunctionReference<
      "mutation",
      "public",
      Record<string, never>,
      any
    >;
  };
  proxyAuth: {
    mintProxyToken: FunctionReference<
      "action",
      "public",
      { clientId?: string; submissionId?: string },
      any
    >;
  };
  publicForms: {
    list: FunctionReference<"query", "public", { firmId: Id<"firms"> }, any>;
    create: FunctionReference<
      "mutation",
      "public",
      { firmId: Id<"firms">; kind?: string; language?: string; name: string },
      any
    >;
    get: FunctionReference<
      "query",
      "public",
      { publicFormId: Id<"publicForms"> },
      any
    >;
    update: FunctionReference<
      "mutation",
      "public",
      {
        endMessages?: Array<{
          body?: string;
          buttonLabel?: string;
          buttonUrl?: string;
          dependsOn?: any;
          key: string;
          label: string;
          title?: string;
        }>;
        intro?: { message?: string; title?: string };
        kind?: string;
        language?: string;
        name?: string;
        publicFormId: Id<"publicForms">;
        status?: string;
      },
      any
    >;
    remove: FunctionReference<
      "mutation",
      "public",
      { publicFormId: Id<"publicForms"> },
      any
    >;
    listResponses: FunctionReference<
      "query",
      "public",
      { publicFormId: Id<"publicForms"> },
      any
    >;
    getResponse: FunctionReference<
      "query",
      "public",
      { submissionId: Id<"submissions"> },
      any
    >;
    setResponseStatus: FunctionReference<
      "mutation",
      "public",
      { status: string; submissionId: Id<"submissions"> },
      any
    >;
    deleteResponse: FunctionReference<
      "mutation",
      "public",
      { submissionId: Id<"submissions"> },
      any
    >;
    getPublicForm: FunctionReference<
      "query",
      "public",
      { publicFormId: Id<"publicForms"> },
      any
    >;
    startPublicSubmission: FunctionReference<
      "mutation",
      "public",
      {
        consentementTexte?: string;
        device?: string;
        preferredLanguage?: string;
        publicFormId: Id<"publicForms">;
      },
      any
    >;
    completePublicSubmission: FunctionReference<
      "mutation",
      "public",
      { piege?: string; submissionId: Id<"submissions"> },
      any
    >;
    getPublicOutcome: FunctionReference<
      "query",
      "public",
      { submissionId: Id<"submissions"> },
      any
    >;
  };
  representants: {
    creer: FunctionReference<
      "mutation",
      "public",
      {
        firmId: Id<"firms">;
        representant: {
          avocatSuperviseur?: string;
          avocatSuperviseurNumeroMembre?: string;
          courriel?: string;
          nom: string;
          numeroMembre?: string;
          organisme: "cicc" | "barreau" | "notaires" | "proche" | "autre";
          organismeAutre?: string;
          prenom: string;
          province?: string;
          remunere: boolean;
        };
      },
      any
    >;
    definirParDefaut: FunctionReference<
      "mutation",
      "public",
      { representantId: Id<"representants"> },
      any
    >;
    definirPourDemande: FunctionReference<
      "mutation",
      "public",
      { demandeId: Id<"demandes">; representantId: Id<"representants"> | null },
      any
    >;
    list: FunctionReference<"query", "public", { firmId: Id<"firms"> }, any>;
    modifier: FunctionReference<
      "mutation",
      "public",
      {
        representant: {
          avocatSuperviseur?: string;
          avocatSuperviseurNumeroMembre?: string;
          courriel?: string;
          nom: string;
          numeroMembre?: string;
          organisme: "cicc" | "barreau" | "notaires" | "proche" | "autre";
          organismeAutre?: string;
          prenom: string;
          province?: string;
          remunere: boolean;
        };
        representantId: Id<"representants">;
      },
      any
    >;
    pourDemande: FunctionReference<
      "query",
      "public",
      { demandeId: Id<"demandes"> },
      any
    >;
    supprimer: FunctionReference<
      "mutation",
      "public",
      { representantId: Id<"representants"> },
      any
    >;
  };
  desaccords: {
    desaccordsDuDossier: FunctionReference<
      "query",
      "public",
      { clientId: Id<"clients"> },
      any
    >;
    reglerLaQuestion: FunctionReference<
      "mutation",
      "public",
      { externalId: string; submissionId: Id<"submissions"> },
      any
    >;
    trierLesDesaccords: FunctionReference<
      "action",
      "public",
      { clientId: Id<"clients"> },
      any
    >;
  };
  lectureDuDossier: {
    lancerLaLecture: FunctionReference<
      "action",
      "public",
      { clientId: Id<"clients">; inclureRepondues?: boolean },
      any
    >;
  };
};
export type InternalApiType = {};
